import {
  NextState,
  TrackerState,
  setErrorAction,
  setAPIErrorAction,
} from './next';
import { Dispatch } from 'redux';

import * as Sentry from '@sentry/browser';

interface Callable {
  (dispatch: Dispatch, getState: () => NextState): Promise<Boolean>;
}

export const queue = (fn: Callable): Callable => {
  let run = [false, false];

  let runner: Callable = async (
    dispatch: Dispatch,
    getState: () => NextState
  ): Promise<Boolean> => {
    let [isRunning] = run;

    if (isRunning) {
      run = [true, true];
      return;
    }

    let next = true;
    while (next) {
      run = [true, false];
      await fn(dispatch, getState);

      let [_, runAgain] = run;
      if (!runAgain) {
        next = false;
      }
    }

    run = [false, false];
  };

  return runner;
};

export const mirror: Callable = async (
  dispatch,
  getState
): Promise<Boolean> => {
  // Get the current tracker state
  const state = getState();
  const tracker = state.tracker;
  const blocks = state.blocks;

  dispatch({type: "MIRROR_ACTIVE", data:true})

  // Reset the tracker for async saving
  const newTracker = Object.keys(tracker)
    .map(k => {
      switch (tracker[k]) {
        case TrackerState.DIRTY:
          return TrackerState.CLEAN;
        case TrackerState.DELETED:
          return undefined;
        default:
          return TrackerState.CLEAN;
      }
    })
    .filter(r => !r);

  dispatch({
    type: 'SET_TRACKER',
    data: newTracker,
  });

  // SAVE and DELETE changes in tracker
  const toUpdate = Object.keys(tracker).filter(
    k => tracker[k] !== TrackerState.CLEAN
  );

  const toSet = toUpdate
    .filter(k => tracker[k] !== TrackerState.CLEAN)
    .map(k => blocks[k])
    .filter(b => b)
    .map(b => {
      if (tracker[b.id] === TrackerState.DELETED) {
        b.__delete = true;
        b.project_id = state.projectId;
        return b;
      } else {
        b.__isSaving = true;
        b.project_id = state.projectId;
        return b;
      }
    });

  let stop = false;

  await window.client.blocks
    .setMany(toSet, state.projectId)
    .then(result => {
      if (Array.isArray(result)) {
        result.forEach(b => {
          if (b.deleted_at) {
            // remove
            dispatch({ type: 'REMOVE_BLOCK_LOCAL', data: b });
          } else {
            // maybe validate
          }
        });
      }
    })
    .catch(err => {
      Sentry.withScope(scope => {
        scope.setExtra('message', err.err);
        Sentry.captureException(err);
      });

      dispatch(setAPIErrorAction(err));

      // RESTORE TRACKER
      const currentStore = getState();
      const currrentTracker = currentStore.tracker;

      const nextTracker = { ...tracker };
      Object.keys(currrentTracker).forEach(k => {
        switch (currrentTracker[k]) {
          case TrackerState.DELETED: {
            nextTracker[k] = TrackerState.DELETED;
            break;
          }
          case TrackerState.DIRTY: {
            nextTracker[k] = TrackerState.DIRTY;
            break;
          }
          default:
            break;
        }
      });

      dispatch({
        type: 'SET_TRACKER',
        data: nextTracker,
      });
    });

  dispatch({type: "MIRROR_ACTIVE", data:false})

  return true;
};

export const Mirror = queue(mirror);
